import {EventType, useCoreAnalytics} from '@smart/analytics';
import type {ISmartEvt14} from '@smart/analytics';
import {useCallback} from 'react';
import {useFeatureAppEnvHook} from '../hooks/use-feature-app-env';
import type {CampaignPopupState} from '../views/campaign-form-popup';
import type {NewsletterPopupState} from '../views/newsletter-form-popup';

type EventStateMap = {
  'subscribe': never;
  'newsletter-form-signUp': NewsletterPopupState;
  'newsletter-form-overlay': NewsletterPopupState;
  'testdrive-overlay': CampaignPopupState;
  'testdrive-signUp': CampaignPopupState;
};

export interface ISmartEvt16 {
  eventInfo: {
    type: `impression`;
    region: string;
    name: `open`;
    url: string;
    form: {
      name: string;
      type: string;
      purpose: string;
    };
  };
}

type AnalyticsEventMap = {
  'subscribe': ISmartEvt14;
  'newsletter-form-signUp': ISmartEvt17;
  'newsletter-form-overlay': ISmartEvt16;
  'testdrive-overlay': ISmartEvt16;
  'testdrive-signUp': ISmartEvt17;
};

type ISmartEvt17 = {
  eventInfo: {
    type: `form`;
    region: string;
    name: `submit.success` | `submit.error`;
    url: string;
    form: {
      name: string;
      type: string;
      purpose: string;
      selectedModel?: string;
      campaignId?: string;
      preferencesFlag?: string;
    };
  };
};

const getAnalyticsInfo = <K extends keyof EventStateMap>(
  campaignId: string,
  eventType?: 'error' | 'success',
  extraParams?: EventStateMap[K],
): AnalyticsEventMap => ({
  'subscribe': {
    eventInfo: {
      type: `form`,
      region: `subscribe`,
      url: window.location.href,
      name: eventType === `error` ? `submit.error` : `submit.success`,
      form: {
        name: campaignId ?? ``,
        type: `subscribe`,
      },
    },
  },
  'newsletter-form-signUp': {
    eventInfo: {
      type: `form`,
      region: `newsletter_overlay`,
      url: window.location.href,
      name: eventType === `error` ? `submit.error` : `submit.success`,
      form: {
        campaignId: campaignId ?? ``,
        name: `newsletter signup`,
        type: `newsletter_overlay`,
        purpose: `smart #5 updates`,
        selectedModel: `#5;${
          (extraParams as NewsletterPopupState)?.lineInterests || ``
        };${(extraParams as NewsletterPopupState)?.colorInterest || ``}`,
        preferencesFlag: (extraParams as NewsletterPopupState)?.noPreference
          ? `1`
          : `0`,
      },
    },
  },
  'newsletter-form-overlay': {
    eventInfo: {
      type: `impression`,
      region: `newsletter_overlay`,
      url: window.location.href,
      name: `open`,
      form: {
        name: `newsletter signup`,
        type: `newsletter_overlay`,
        purpose: `smart #5 updates`,
      },
    },
  },
  'testdrive-overlay': {
    eventInfo: {
      type: `impression`,
      region: `testdrive_overlay`,
      url: window.location.href,
      name: `open`,
      form: {
        name: `test-drive`,
        type: `test_drive`,
        purpose: `smart #5 Test Drive`,
      },
    },
  },
  'testdrive-signUp': {
    eventInfo: {
      type: `form`,
      region: `testdrive_overlay`,
      url: window.location.href,
      name: eventType === `error` ? `submit.error` : `submit.success`,
      form: {
        campaignId: campaignId ?? ``,
        name: `test-drive`,
        type: `test_drive`,
        purpose: `smart #5 Test Drive`,
        preferencesFlag: (extraParams as CampaignPopupState)?.newsletterConsent
          ? `1`
          : `0`,
      },
    },
  },
});

export const useAnalytics = (campaignId?: string | undefined) => {
  const provideAnalytics = useCoreAnalytics(useFeatureAppEnvHook, `forms`);

  return useCallback(
    <T extends NewsletterPopupState | CampaignPopupState>(
      eventType: 'success' | 'error',
      key: keyof AnalyticsEventMap,
      extraParams?: T,
    ) => {
      const event = getAnalyticsInfo(campaignId ?? ``, eventType, extraParams);
      provideAnalytics(
        event[key] as ISmartEvt17 | ISmartEvt14,
        EventType.CLICK,
      );
    },
    [campaignId],
  );
};
